import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { PremiumEntity } from '@core/store/entities/premium/premium.entity';
import { ProductType } from '@core/models/api/dsm-types';
import { CoverageEntity } from '@core/store/entities/coverage/coverage.entity';
import { CoverageModel } from '@core/models/views/coverage.model';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil, map, withLatestFrom, debounceTime } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ProductModel } from '@core/store/entities/product/product.model';
import { CoveredLocationEntity } from '@core/store/entities/covered-location/covered-location.reducer';
import { CurrencyPipe } from '@angular/common';
import { DateUtils } from '@shared/utils/date.utils';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from '@core/services/quote.service';
import { ProductsService } from '@core/services/products.service';
import { ComponentChanges } from '@shared/utils/general.utils';
import { Nullable } from '@shared/utils/type.utils';
import { PolicyDateOptions } from '@core/interfaces/interfaces';
import { DatepickerInputComponent } from '@shared/components/datepicker-input/datepicker-input.component';
import { ErrorModel } from '@entities/error/error.model';
import { rateQuoteFail } from '@entities/quote/quote.action';
import { TaskService } from '@core/services/task.service';
import { DeleteProductService } from '@core/services/delete-product.service';
import { PriceSummaryDisplayProduct } from '../price-summary-display/price-summary-display-content';
import { CoverageRulesService } from '@core/services/coverage-rules.service';

interface HomeownersCoverageDisplayFormModel {
  effectiveDate: Nullable<string>; // "MM/DD/YYYY"
}

@Component({
  selector: 'nwx-homeowner-coverage-display',
  templateUrl: './homeowner-coverage-display.component.html',
  styleUrls: ['./homeowner-coverage-display.component.scss'],
  standalone: false,
})
export class HomeownerCoverageDisplayComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() premiums!: PremiumEntity[];
  @Input() coverages!: CoverageEntity[];
  @Input() product!: ProductModel;
  @Input() coveredLocation?: CoveredLocationEntity;
  @Input() maxSizeMini!: boolean;
  @Input() errors: ErrorModel[] = [];
  @Input() priceSummaryDisplayProduct!: PriceSummaryDisplayProduct;

  form = new FormGroup({
    effectiveDate: new FormControl('', [
      (control) =>
        DatepickerInputComponent.validate(
          control,
          this.policyDateOptions?.minDate || '',
          this.policyDateOptions?.maxDate || ''
        ),
    ]),
  });
  policyDateOptions: Nullable<PolicyDateOptions> = null;

  totalPolicyPremium = 0;
  formattedAddress = '';
  yearRoofReplaced: number | string = 0;
  showDatePicker: boolean = false;
  formValueDate: any;
  displayCoverages: CoverageModel[] = [];
  displayDeductibles: CoverageModel[] = [];
  updateInProgress$: Observable<boolean>;
  private unsubscribe$ = new Subject<void>();
  showPremium = false;

  displayCoverageIds: string[] = [
    'PersonalLiability',
    'MedicalPayments',
    'UnscheduledPersonalProperty',
    'BasicCoverage',
    'DwellingReplacementCost',
    'IncreasedOtherStructures',
    'IncreasedLossOfUse',
    'GuaranteedReplacementCostPlus'
  ];

  deductibleCoverageIds: string[] = [
    'UnscheduledPersonalProperty',
    'SectionIDeductibles',
  ];

  deductibleCodes: string[] = [
    'AllPerilDeductible',
    'UnscheduledPersonalPropertyDeductible',
  ];

  constructor(
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private quoteService: QuoteService,
    private modalService: NgbModal,
    private productsService: ProductsService,
    private ref: ChangeDetectorRef,
    private taskService: TaskService,
    private deleteProductService: DeleteProductService
  ) {
    this.updateInProgress$ = this.quoteService
      .getQuoteUpdateInProgress()
      .pipe(map((v) => !!v));
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.updateInProgress$)
      )
      .subscribe(this.onFormChanged.bind(this));
  }

  ngOnDestroy(): void {
    this.form.disable();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.showDatePicker = false;
  }

  ngOnInit(): void {
    this.totalPolicyPremium =
      this.premiums.find((p) => p.productType === 'Homeowner')?.total?.amount ||
      0;
    this.formatAddress();
    this.getRoofReplacementYear();
    this.getDisplayCoverages();
    this.productsService
      .getPolicyDateOptions(this.product.type)
      .pipe(debounceTime(100), takeUntil(this.unsubscribe$))
      .subscribe((options) => {
        this.policyDateOptions = options;
        this.ref.detectChanges();
      });
  }

  ngOnChanges(
    changes: ComponentChanges<HomeownerCoverageDisplayComponent>
  ): void {
    if (changes.product) {
      this.form.patchValue({
        effectiveDate: DateUtils.formatDsmDateToOld(
          this.product.effectiveDate || ''
        ),
      });
      this.showDatePicker = false;
    }
    if (
      changes.premiums ||
      changes.errors ||
      changes.priceSummaryDisplayProduct
    ) {
      if (
        this.errors.find(
          (e) =>
            e.productType === 'Homeowner' &&
            e.sourceActionType === rateQuoteFail.type &&
            !e.developerMessage?.includes('EVTAlerts')
        )
      ) {
        this.showPremium = false;
      } else if (!this.priceSummaryDisplayProduct.installmentPrice) {
        this.showPremium = false;
      } else {
        this.showPremium = true;
      }
    }
  }

  ngDoCheck(): void {
    if (this.formValueDate !== this.form.value.effectiveDate) {
      this.formValueDate = this.form.value.effectiveDate;
    }
  }

  private formatAddress(): void {
    const location = this.coveredLocation?.location;
    if (location) {
      const state = location.state?.toUpperCase();
      const addressLine = this.toTitleCase(location.addressLine1 || '');
      const city = this.toTitleCase(location.city || '');
      const postal = this.toTitleCase(location.postalCode || '');

      this.formattedAddress = `${addressLine}, ${city}, ${state} ${postal?.substring(
        0,
        5
      )}`;
    }
  }

  private toTitleCase(text: string): string {
    return text
      .toLocaleLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getRoofReplacementYear(): void {
    const roofRenovation = this.coveredLocation?.renovations?.find(
      (r) => r.type === 'Roof'
    );
    this.yearRoofReplaced =
      roofRenovation?.year ||
      // (this.coveredLocation.constructionInfo?.yearBuilt as number) ||
      'unknown';
  }

  getDisplayCoverages(): void {
    this.displayCoverages = [];
    this.displayDeductibles = [];
    const displayEntities = this.coverages.filter(
      (c) =>
        c.productId === 'Homeowner' &&
        this.displayCoverageIds.includes(c.coverageId)
    );
    displayEntities.forEach((cov) => {
      const val = cov?.selectedValue?.[0]?.value || '';
      const desc = cov?.selectedValue?.[0]?.description || '';

      if (desc) {
        this.displayCoverages.push({
          name: cov.name,
          value: desc,
        });
      } else if (this.isNumber(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: this.currencyPipe.transform(val) || '',
        });
      } else if (this.isAcceptDecline(val)) {
        this.displayCoverages.push({
          name: cov.name,
          value: val === 'true' ? 'Accept' : 'Decline',
        });
      }
    });

    this.displayDeductibles = [];
    for (const coverage of this.coverages.filter(
      (c) =>
        c.productId === 'Homeowner' &&
        c.available &&
        !c.selectedValue?.find(
          (v) => v.code === 'selected' && v.value === 'false'
        )
    )) {
      if (this.displayCoverageIds.includes(coverage.coverageId)) {
        continue;
      }
      let value = '';
      // A few specific terms for which we want value, not description.
      let selectedValue = coverage.selectedValue?.find(
        (v) =>
          v.code === 'AllPerilDeductible' ||
          v.code === 'UnscheduledPersonalProperty' ||
          v.code === 'ProtectionBoostLevel'
      );
      if (selectedValue) {
        value = selectedValue.value;
      } else {
        selectedValue = coverage.selectedValue?.[0];
        if (selectedValue) {
          value = selectedValue.description || 'Selected';
        } else {
          value = 'Selected';
        }
      }
      this.displayDeductibles.push({
        name: coverage.name,
        value,
      });
    }
  }

  isNumber(val: string | number): boolean {
    return parseInt(`${val}`) ? true : false;
  }

  isAcceptDecline(val: string): boolean {
    return val === 'false' || val === 'true';
  }

  editCoverages(): void {
    this.router.navigate(['/quote/coverages'], {
      queryParams: { product: 'Homeowner' } as { product: ProductType },
    });
  }
  // this is no longer correct
  routeToHome(): void {
    this.router.navigate(['quote/property/homeowner'], {});
  }

  promptToRemovePolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndRemoveProduct(this.product);
  }

  promptToWithdrawPolicy(event?: Event): void {
    event?.preventDefault();
    this.deleteProductService.promptAndWithdrawProduct(this.product);
  }

  private onFormChanged(
    value: [Partial<HomeownersCoverageDisplayFormModel>, Nullable<boolean>]
  ): void {
    const updateInprogress = value[1] === true;
    const newDate = DateUtils.formatDateToDSM(value[0]?.effectiveDate);
    if (
      updateInprogress ||
      !this.product ||
      this.product.effectiveDate === newDate ||
      !this.form.valid
    ) {
      return;
    }
    this.productsService.updateQuote({
      productType: 'Homeowner',
      effectiveDate: newDate,
    });
  }

  scrollToTasks(): void {
    this.taskService.setShowTasks(true);
    const div = document.querySelector('.tasks-container');
    if (div) div.scrollIntoView({ behavior: 'smooth' });
  }
}
